import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SpinningButton } from "Components/ReactstrapSlim";
import { PasswordInputFL, ValidationSummary } from "Components/Inputs";
import { useApiClient } from "Services/API/useApiClient";
import { AccountClient, LogonSuccessResponse, isChangePasswordSuccessResponse } from "Services/LegacyAPI/AccountClient";

type ChangePasswordFormModel = Pick<LSWebUI.Models.ForgotPasswordViewModel, "OldPassword" | "NewPassword" | "ConfirmPassword">;

const ChangePasswordFormSchema: yup.SchemaOf<ChangePasswordFormModel> = yup.object({
   OldPassword: yup.string()
      .required("Current password is required")
      .defined(),
   NewPassword: yup.string()
      .required("New password is required")
      .min(LiquidSpaceConfig.NewMinPasswordLength, "Password must be at least ${min} characters long")
      .max(LiquidSpaceConfig.MaxPasswordLength, "Enter no more than ${max} characters")
      .test("no-spaces", "Password shouldn't contain spaces", (value) =>
         !value || !value.includes(" "))
      .notOneOf([yup.ref("OldPassword")], "Password must be different from the current")
      .defined(),
   ConfirmPassword: yup.string()
      .required("Confirmation password is required")
      .oneOf([yup.ref("NewPassword"), null], "Passwords must match")
      .defined(),
}).defined();

export interface ChangePasswordFormProps extends LSWebUI.Models.ForgotPasswordViewModel {
   isActive: boolean;
   onSuccess: (logonResult: LogonSuccessResponse) => boolean;
   returnUrl?: string,
}

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = function ({ isActive, UserName, onSuccess, OldPassword, ForcePasswordChange, returnUrl }) {
   const accountClient = useApiClient(AccountClient);
   const [isLoading, setIsLoading] = useState<boolean>(false);
   const [serverErrors, setServerErrors] = useState<Array<[string, string]>>([]);

   const formMethods = useForm<ChangePasswordFormModel>({
      defaultValues: {
         OldPassword: OldPassword || "",
      },
      resolver: yupResolver(ChangePasswordFormSchema),
      mode: "onBlur"
   });

   const onSubmit = async () => {
      if (isLoading)
         return;
      setIsLoading(true);
      setServerErrors([]);
      try {
         const isValid = await formMethods.trigger();
         if (isValid) {
            const formValues = formMethods.getValues();

            const changePasswordRequest: LSWebUI.Models.ForgotPasswordViewModel = {
               UserName: UserName,
               ForcePasswordChange: false,
               ...formValues,
            };
            const changeResult = await accountClient.forgotPassword(changePasswordRequest);
            if (changeResult.success) {
               if (isChangePasswordSuccessResponse(changeResult)) {
                  window.location.href = `/account/registerconfirmation?id=${changeResult.memberId}&changePassword=false&showTermsOfUse=true&returnUrl=${returnUrl}`;
               } else {
                  onSuccess(changeResult);
               }
            } else {
               if (changeResult.errors && changeResult.errors.length)
                  setServerErrors(changeResult.errors.map(err => ["newPassword", err]));
               else
                  setServerErrors([["newPassword", "Unexpected server error"]]);
            }
         }
      } catch (err: any) {
         if (err.error) {
            setServerErrors([["newPassword", err.error]]);
         }
         console.log(err);
      } finally {
         setIsLoading(false);
      }
   }

   const submitOnEnterHandler = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
         onSubmit();
      }
   }

   useEffect(() => {
      if (isActive) {
         setTimeout(function () {
            formMethods.setFocus(OldPassword ? "NewPassword" : "OldPassword");
         }, 500);
      }
   }, [isActive])

   return (
      <div className="signing-mode-inner signing-mode-inner--centered">
         <div className="signing-header">
            Set a new password
         </div>
         <div className="signing-caption">
            {!OldPassword && <div>We sent you an email with instructions on how to reset your password.</div>}
            <div>If you have any questions, contact <a href="mailto:support@liquidspace.com">support@liquidspace.com</a></div>
         </div>
         {
            (OldPassword || !ForcePasswordChange) &&
            <div className="signing-change-password-form">
               <ValidationSummary errors={formMethods.formState.errors} serverErrors={serverErrors} className="text-center" />
               {
                  OldPassword
                     ? <input {...formMethods.register("OldPassword")} type="hidden" />
                     : <PasswordInputFL control={formMethods.control} name="OldPassword" placeholder="Temporary password" className="scpf-input" autoComplete="off" onKeyDown={submitOnEnterHandler} />
               }
               <PasswordInputFL control={formMethods.control} name="NewPassword" placeholder="New password" className="scpf-input" autoComplete="off" onKeyDown={submitOnEnterHandler} />
               <PasswordInputFL control={formMethods.control} name="ConfirmPassword" placeholder="Repeat new password" className="scpf-input" autoComplete="off" onKeyDown={submitOnEnterHandler} />
               <SpinningButton isLoading={isLoading} onClick={onSubmit} color="primary" size="md" fluid className="scpf-cta">Set password</SpinningButton>
            </div>
         }
      </div>
   )
}